import AWS from 'aws-sdk'
import { trackEvent } from '../utils/telemetry'
import { flagEnabled, PRESIGNED_UPLOAD } from '../utils/featureFlags'
import { getEdgeUploadUrl, getEdgeGetSessionUrl } from '../utils/edge'
import { suppliedImages } from '../utils/suppliedImages'

function parseImageType(imageKey) {
    if (!imageKey) return "unknown";
    if (imageKey.includes("_dl_front")) return "id_front";
    if (imageKey.includes("_dl_back")) return "id_back";
    if (imageKey.includes("_dl_selfie")) return "selfie";
    return "other";
}

async function fetchServerClockOffset() {
    try {
        const res = await fetch(window.location.origin, { method: 'HEAD' });
        const serverDate = res.headers.get('date');
        if (serverDate) {
            return Date.parse(serverDate) - Date.now();
        }
    } catch (e) { /* ignore */ }
    return 0;
}

// image_type the upload endpoint accepts, derived from the key the callers
// already build (`${request_id}_dl_${suffix}.jpeg`).
export function parseUploadImageType(imageKey) {
    const m = /_dl_(front|back|selfie)\.jpe?g$/i.exec(imageKey || "");
    return m ? m[1].toLowerCase() : null;
}

// A PUT that has not finished in this long is a dead connection, not a slow
// one: p95 for a 1-2MB image is a few seconds and the URL itself expires at
// 120s. Abort and retry on a fresh URL instead of waiting on the browser's
// own (multi-minute) timeout.
export const PRESIGNED_PUT_TIMEOUT_MS = 30000;
// PUT attempts in total (first try plus retries), each retry on a fresh URL.
export const PRESIGNED_PUT_ATTEMPTS = 3;

/**
 * The upload token is minted at session load with a short TTL, and every
 * GET get_session poll re-mints it. A customer who spends longer than the
 * TTL on capture gets a 403 from the presign endpoint; one poll gives us a
 * fresh token without changing the TTL. Mutates `settings` so the next
 * image in the same session reuses the refreshed token.
 */
export async function refreshUploadToken(settings, fetchImpl = fetch) {
    if (!settings?.request_id) return false;
    const res = await fetchImpl(`${getEdgeGetSessionUrl()}/${settings.request_id}`, { method: "GET" });
    if (!res.ok) return false;
    const json = await res.json();
    const token = json?.data?.settings?.upload_token || json?.settings?.upload_token;
    if (!token) return false;
    settings.upload_token = token;
    return true;
}

/**
 * Presigned path: ask the edge for a one-object, 120s PUT URL and send the
 * bytes straight to S3 with exactly the headers the server signed. The
 * browser never holds AWS credentials. Throws on any problem so the caller
 * can fall back to the Cognito path during the rollout (never on a journey
 * API prefill session, see uploadImageToS3).
 */
export async function uploadViaPresignedUrl(imageKey, imageFile, settings, contentType, metadata) {
    const imageType = parseUploadImageType(imageKey);
    if (!imageType) throw new Error("unsupported_image_key");
    if (!settings?.upload_token) throw new Error("no_upload_token");

    // Size travels in the request so the server can cap it and sign
    // Content-Length; the PUT then has to carry exactly these bytes.
    const contentLength = imageFile?.size;
    if (!Number.isInteger(contentLength) || contentLength <= 0) throw new Error("no_content_length");

    const body = {
        request_id: settings.request_id,
        image_type: imageType,
        content_type: contentType,
        content_length: contentLength,
        ...(metadata ? { metadata } : {}),
    };

    // Presign: one retry on a network-level failure of the call.
    const sign = () => fetch(getEdgeUploadUrl(), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Upload-Token": settings.upload_token,
        },
        body: JSON.stringify(body),
    });
    let tokenRefreshed = false;
    const presign = async () => {
        let signRes;
        try {
            signRes = await sign();
        } catch (firstError) {
            trackEvent("presigned_upload_retry", { image_type: parseImageType(imageKey), stage: "presign", error_type: firstError?.message || "unknown" });
            await new Promise((resolve) => setTimeout(resolve, 1000));
            signRes = await sign();
        }
        // 403 = the upload token expired (long capture, resumed tab). Pull a
        // fresh one from the session poll and sign once more before failing.
        if (signRes.status === 403 && !tokenRefreshed) {
            tokenRefreshed = true;
            trackEvent("presigned_upload_retry", { image_type: parseImageType(imageKey), stage: "token_refresh", error_type: "upload_url_403" });
            let refreshed = false;
            try { refreshed = await refreshUploadToken(settings); } catch (e) { refreshed = false; }
            if (refreshed) signRes = await sign();
        }
        if (!signRes.ok) throw new Error(`upload_url_${signRes.status}`);
        const presigned = await signRes.json();
        if (!presigned?.url) throw new Error("upload_url_empty");
        return presigned;
    };

    // PUT with exactly the signed headers. The server signs Content-Length,
    // but browsers refuse a manually set Content-Length on fetch and compute
    // it from the body; the blob is the size we sent in content_length, so
    // the signature still matches.
    //
    // Retries: a PUT that throws (no response) or hangs past
    // PRESIGNED_PUT_TIMEOUT_MS is retried up to PRESIGNED_PUT_ATTEMPTS times,
    // each time on a FRESH presigned URL. Reusing the URL was wrong: a hung
    // PUT usually outlives the URL's 120s window, so the retry came back 403
    // and cost the user minutes. A 403 on an otherwise healthy PUT (clock
    // skew, paused tab) also gets one fresh URL. Any other non-2xx is final.
    const putOnce = async ({ url, method, headers }) => {
        const putHeaders = { "Content-Type": contentType, ...(headers || {}) };
        delete putHeaders["Content-Length"];
        delete putHeaders["content-length"];
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), PRESIGNED_PUT_TIMEOUT_MS) : null;
        try {
            return await fetch(url, { method: method || "PUT", headers: putHeaders, body: imageFile, ...(controller ? { signal: controller.signal } : {}) });
        } finally {
            if (timer) clearTimeout(timer);
        }
    };

    let presigned = await presign();
    let putRes = null;
    let represigned = false;
    for (let attempt = 1; attempt <= PRESIGNED_PUT_ATTEMPTS; attempt++) {
        try {
            putRes = await putOnce(presigned);
        } catch (error) {
            const errorType = error?.name === "AbortError" ? `put_timeout_${PRESIGNED_PUT_TIMEOUT_MS}ms` : (error?.message || "unknown");
            if (attempt === PRESIGNED_PUT_ATTEMPTS) throw new Error(errorType);
            trackEvent("presigned_upload_retry", { image_type: parseImageType(imageKey), stage: "put", attempt, error_type: errorType });
            await new Promise((resolve) => setTimeout(resolve, 1000));
            presigned = await presign();
            continue;
        }
        if (putRes.status === 403 && !represigned) {
            represigned = true;
            trackEvent("presigned_upload_retry", { image_type: parseImageType(imageKey), stage: "represign", attempt, error_type: "s3_put_403" });
            presigned = await presign();
            continue;
        }
        break;
    }
    if (!putRes.ok) throw new Error(`s3_put_${putRes.status}`);
    const url = presigned.url;

    // Same shape the aws-sdk upload() callback resolved with, for callers
    // that read Location/Key.
    return { Location: url.split("?")[0], Key: `upload/${imageKey}`, Bucket: null, path: "presigned" };
}

export const uploadImageToS3 = async (imageKey, imageFile, settings = {}, contentType = 'image/jpeg', metadata = null) => {

    // Journey API prefill (utils/suppliedImages.js): get-session limits the
    // upload token to the images the user still captures, so a supplied image
    // cannot be overwritten. The Cognito credentials can write any key, so
    // these sessions upload presigned only, whatever the rollout flag says,
    // and a failure surfaces as an upload error instead of falling back.
    const presignedOnly = suppliedImages(settings).length > 0;

    if (presignedOnly || (flagEnabled(PRESIGNED_UPLOAD) && settings?.upload_token)) {
        const started = performance.now();
        try {
            const result = await uploadViaPresignedUrl(imageKey, imageFile, settings, contentType, metadata);
            trackEvent("upload_presigned", {
                image_type: parseImageType(imageKey),
                upload_ms: Math.round(performance.now() - started),
                bytes: imageFile?.size ?? null,
            });
            return result;
        } catch (error) {
            // Fall through to the Cognito path so a broken edge never blocks
            // a verification during the ramp; the event tells us how often.
            trackEvent("presigned_upload_failed", {
                error_type: error?.message || "unknown",
                image_type: parseImageType(imageKey),
                fallback: !presignedOnly,
            });
            if (presignedOnly) throw error;
        }
    }

    const bucketName = settings?.bucket_name || "dcamsv3"
    const bucketRegion = settings?.bucket_region || "us-east-2"
    const IdentityPoolId = settings?.identity_pool_id || "us-east-2:d6381cdb-1612-4dd1-ad4a-c8b28de1bd8b"
    const albumName = "upload";

    async function doUpload() {
        AWS.config.update({
            region: bucketRegion,
            correctClockSkew: true,
        })

        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: IdentityPoolId
        })

        const s3 = new AWS.S3({
            region: bucketRegion,
            correctClockSkew: true,
        })

        var albumPhotosKey = encodeURIComponent(albumName) + "/";
        var photoKey = albumPhotosKey + imageKey;

        const params = {
            Bucket: bucketName,
            Key: photoKey,
            Body: imageFile,
            ContentType: contentType,
            ...(metadata ? { Metadata: metadata } : {}),
        }

        return new Promise((resolve, reject) => {
            s3.upload(params, (error, data) => {
                if (error) reject(error)
                else resolve(data)
            })
        })
    }

    try {
        const started = performance.now();
        const result = await doUpload();
        // Mirror of upload_presigned so every upload carries exactly one
        // path event during the ramp; the ratio is the readiness signal for
        // retiring the Cognito pool.
        trackEvent("upload_cognito", {
            image_type: parseImageType(imageKey),
            upload_ms: Math.round(performance.now() - started),
            bytes: imageFile?.size ?? null,
        });
        return result;
    } catch (error) {
        if (error.code === 'RequestTimeTooSkewed') {
            AWS.config.systemClockOffset = await fetchServerClockOffset();
            try {
                return await doUpload();
            } catch (retryError) {
                trackEvent("s3_upload_failed", {
                    error_type: retryError?.code || retryError?.message || "unknown",
                    image_type: parseImageType(imageKey),
                    retried: true,
                });
                throw retryError;
            }
        }
        trackEvent("s3_upload_failed", {
            error_type: error?.code || error?.message || "unknown",
            image_type: parseImageType(imageKey),
            retried: false,
        });
        throw error;
    }
}
